import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { api } from "../lib/api.ts";
import { toast } from "../lib/store.ts";

export interface FeedbackItem {
  id: string;
  createdAt: string;
  lotId: string | null;
  lotTitle: string | null;
  lotUrl: string | null;
  page: string | null;
  body: string;
  status: "open" | "working" | "done" | "wontfix";
  reply: string | null;
  repliedAt: string | null;
}

const STATUS: Record<FeedbackItem["status"], string> = { open: "Waiting for Claude", working: "Claude is on it", done: "Done", wontfix: "Not changing" };

const useFeedback = () => useQuery({ queryKey: ["feedback"], queryFn: () => api<{ items: FeedbackItem[]; lastReadAt: string | null }>("/feedback") });

/** A note to Claude. With a lot, the note carries the lot and the page Hammer last received for it. */
export function FeedbackBox({ lotId, lotTitle, compact, onSent }: { lotId?: string | null; lotTitle?: string | null; compact?: boolean; onSent?: () => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      await api("/feedback", { method: "POST", body: { body: text.trim(), lotId: lotId ?? null, page: location.pathname } });
      setText("");
      void qc.invalidateQueries({ queryKey: ["feedback"] });
      toast("Sent. Claude will see it next time you ask it to check your notes.");
      onSent?.();
    } catch (e) {
      toast(`Couldn't send: ${(e as Error).message}`, { tone: "error" });
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="grid gap-2" onSubmit={(e) => { e.preventDefault(); void send(); }}>
      {lotTitle && <p className="text-[13px] muted">About: <span className="text-ink">{lotTitle}</span> (the lot and the page Hammer received are attached)</p>}
      <textarea
        className="field"
        rows={compact ? 3 : 5}
        placeholder={lotId ? "What's wrong with this lot? E.g. the price says $5,000 but the site shows $100,000." : "What's wrong, or what should change? Be as blunt as you like."}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void send(); } }}
      />
      <div><button className="btn btn-primary" disabled={!text.trim() || busy}>{busy ? "Sending…" : "Send to Claude"}</button></div>
    </form>
  );
}

export function Feedback() {
  const { lot } = useSearch({ strict: false }) as { lot?: string };
  const qc = useQueryClient();
  const q = useFeedback();
  const lotQ = useQuery({ queryKey: ["lot-title", lot], enabled: !!lot, queryFn: () => api<{ title: string }>(`/lots/${lot}`) });
  const [showDone, setShowDone] = useState(false);
  const items = q.data?.items ?? [];
  const open = items.filter((f) => f.status === "open" || f.status === "working");
  const done = items.filter((f) => f.status === "done" || f.status === "wontfix");

  const remove = async (id: string) => {
    await api(`/feedback/${id}`, { method: "DELETE" });
    void qc.invalidateQueries({ queryKey: ["feedback"] });
  };

  return (
    <div className="grid gap-8 max-w-[40rem]">
      <div>
        <h1 className="heading text-[26px]">Tell Claude</h1>
        <p className="muted text-[14px] mt-1">
          Write anything that's wrong or that you want changed. Then, in a Claude Code session on the auctiontracker repo, say <b>"check my Hammer notes"</b>. Claude reads them here, fixes things, and replies under each one.
        </p>
      </div>

      <FeedbackBox lotId={lot} lotTitle={lotQ.data?.title} />

      <section className="grid gap-3">
        <h2 className="heading text-[18px]">Waiting{open.length ? ` (${open.length})` : ""}</h2>
        {open.length ? <ul className="grid gap-3">{open.map((f) => <Note key={f.id} f={f} onDelete={() => void remove(f.id)} />)}</ul> : <p className="muted text-[14px]">Nothing waiting.</p>}
      </section>

      {done.length > 0 && (
        <section className="grid gap-3">
          <button className="heading text-[18px] text-left" aria-expanded={showDone} onClick={() => setShowDone(!showDone)}>{showDone ? "▾" : "▸"} Answered ({done.length})</button>
          {showDone && <ul className="grid gap-3">{done.map((f) => <Note key={f.id} f={f} onDelete={() => void remove(f.id)} />)}</ul>}
        </section>
      )}

      <ClaudeKey lastReadAt={q.data?.lastReadAt ?? null} />
    </div>
  );
}

function Note({ f, onDelete }: { f: FeedbackItem; onDelete: () => void }) {
  return (
    <li className="panel grid gap-2">
      <div className="flex justify-between gap-3 items-start">
        <div className="text-[13px] muted">
          {new Date(f.createdAt).toLocaleString()} · <span className={f.status === "done" ? "text-ink" : ""}>{STATUS[f.status]}</span>
          {f.lotTitle && <> · {f.lotId ? <Link to="/lot/$id" params={{ id: f.lotId }} className="underline underline-offset-2">{f.lotTitle}</Link> : f.lotTitle}</>}
        </div>
        <button className="icon-btn icon-btn-danger" aria-label="Delete note" title="Delete note" onClick={onDelete}>✕</button>
      </div>
      <p className="whitespace-pre-wrap text-[15px]">{f.body}</p>
      {f.reply && (
        <div className="border-l-2 border-[var(--paddle)] pl-3">
          <p className="text-[13px] muted">Claude{f.repliedAt ? `, ${new Date(f.repliedAt).toLocaleString()}` : ""}</p>
          <p className="whitespace-pre-wrap text-[15px]">{f.reply}</p>
        </div>
      )}
    </li>
  );
}

/** The key Claude uses to read notes. It goes in the Claude environment's settings, never in a chat. */
function ClaudeKey({ lastReadAt }: { lastReadAt: string | null }) {
  const qc = useQueryClient();
  const [show, setShow] = useState(false);
  const q = useQuery({ queryKey: ["claude-key"], queryFn: () => api<{ key: string; origin: string }>("/claude-key"), enabled: show });
  const [copied, setCopied] = useState(false);
  return (
    <section className="grid gap-3">
      <h2 className="heading text-[18px]">Let Claude read these (one-time setup)</h2>
      <p className="text-[14px]">{lastReadAt ? <>Claude last read your notes {new Date(lastReadAt).toLocaleString()}.</> : <>Claude hasn't read them yet.</>}</p>
      <ol className="list-decimal pl-5 grid gap-1 text-[14px]">
        <li>Copy the key below.</li>
        <li>In Claude Code on the web, open the environment menu in the session's title bar and choose Edit.</li>
        <li>Add an environment variable named <code className="num">HAMMER_CLAUDE_KEY</code> with the key as its value, and save. New sessions pick it up.</li>
      </ol>
      <p className="text-[13px] muted">Don't paste the key into a chat. It only lets Claude read lots and notes and reply to notes; it can't log in or change your lots.</p>
      {!show ? (
        <div><button className="btn" onClick={() => setShow(true)}>Show key</button></div>
      ) : q.data ? (
        <>
          <pre className="panel text-[12.5px] whitespace-pre-wrap break-all num">{q.data.key}</pre>
          <div className="flex flex-wrap gap-2">
            <button className="btn btn-primary" onClick={async () => { await navigator.clipboard.writeText(q.data!.key); setCopied(true); setTimeout(() => setCopied(false), 2500); }}>{copied ? "Copied" : "Copy key"}</button>
            <button className="btn btn-quiet" onClick={async () => {
              await api("/claude-key/rotate", { method: "POST" });
              await qc.invalidateQueries({ queryKey: ["claude-key"] });
              toast("New key made. Update HAMMER_CLAUDE_KEY; the old one stops working.");
            }}>Make a new key</button>
          </div>
        </>
      ) : <div className="skeleton h-8 w-60" aria-busy />}
    </section>
  );
}
